/**
 * Privacy ("Gizli mod") — verdeckt alle Geldbeträge im Panel.
 *
 * User-Wunsch 21.07.2026: ein Auge-Schalter im Dashboard-Kopf, ein Klick
 * verdeckt JEDEN Betrag (`₺✱✱✱✱`), zweiter Klick zeigt sie wieder.
 * Zweck: Schulterblick-Schutz beim Arbeiten in der Öffentlichkeit / beim Teilen
 * des Bildschirms.
 *
 * Warum ZENTRAL (Context + <Amount>) statt pro Seite:
 *   F96-Lehre aus dem Audit — zwei Kopien derselben Anzeige-Logik driften
 *   auseinander. Jede Betragsanzeige läuft durch <Amount>, damit ein einziger
 *   Ort entscheidet wie formatiert und wie maskiert wird.
 *
 * Regeln:
 *  - Persistenz: localStorage 'sp-privacy' ('1' = versteckt). Überlebt Neuladen
 *    wie das Theme, gilt global — nicht pro Seite.
 *  - SSR-safe: kein window/localStorage im Modul-Scope
 *    (Hardcode feedback_no_module_scope_window_ssr). useSyncExternalStore liest
 *    beim ersten Client-Render synchron, damit die Beträge beim Laden nicht
 *    kurz aufblitzen — ein useEffect-Sync wäre genau dafür einen Frame zu spät.
 *  - Kein Layout-Sprung: der maskierte Span bekommt die Breite des echten
 *    Textes als min-width in `ch`. Der Wert ist bekannt, also braucht es keine
 *    DOM-Messung.
 *  - Maskiert wird nur die ANZEIGE. Werte, Exporte, PDFs und API-Antworten
 *    bleiben unberührt.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react'
import * as m from '@/paraglide/messages'
import { AnimatedNumber } from './animated-number'

const STORAGE_KEY = 'sp-privacy'
const MASK = '✱✱✱✱'

// ─── Store (localStorage + cross-tab sync) ─────────────────────────────────

const listeners = new Set<() => void>()

function emit() {
  for (const l of listeners) l()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  // 'storage' feuert nur in ANDEREN Tabs — hält zwei offene Panel-Tabs synchron.
  if (typeof window !== 'undefined') window.addEventListener('storage', listener)
  return () => {
    listeners.delete(listener)
    if (typeof window !== 'undefined') window.removeEventListener('storage', listener)
  }
}

function getSnapshot(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

/** Server rendert immer sichtbar — sonst Hydration-Mismatch. */
function getServerSnapshot(): boolean {
  return false
}

function setStored(hidden: boolean) {
  try {
    window.localStorage.setItem(STORAGE_KEY, hidden ? '1' : '0')
  } catch {
    /* ignore storage errors (Safari private mode) */
  }
  emit()
}

// ─── Context ───────────────────────────────────────────────────────────────

interface PrivacyContextValue {
  /** true = Beträge sind verdeckt */
  hidden: boolean
  toggle: () => void
  /**
   * Für Stellen, an denen ein STRING gebraucht wird und <Amount> nicht
   * einsetzbar ist: title-Attribute, SVG-<text>, i18n-Platzhalter
   * (`m.geliver_balance({ balance: mask(...) })`).
   *
   * Ohne das lecken genau diese Stellen den Betrag weiter — die Skeptiker
   * haben das am 21.07. in sechs Dateien nachgewiesen (Hover-Tooltip zeigte
   * den echten Kontostand, während die Kachel daneben maskiert war).
   */
  mask: (text: string) => string
  /** Übersetztes Wort für „verborgen", z.B. als aria-label. */
  maskedLabel: string
}

const PrivacyContext = createContext<PrivacyContextValue>({
  hidden: false,
  toggle: () => {},
  mask: (text) => text,
  // Getter, kein fester Wert: dieses Objekt wird beim Laden des Moduls EINMAL
  // ausgewertet — ein direkter m.privacy_masked()-Aufruf fröre die Sprache auf
  // den Stand des ersten Imports ein. Vorher stand hier hart 'Gizli', was in
  // de/en/ru als türkisches aria-label durchschlug.
  get maskedLabel() {
    return m.privacy_masked()
  },
})

export function usePrivacy(): PrivacyContextValue {
  return useContext(PrivacyContext)
}

export function PrivacyProvider({
  children,
  /**
   * Übersetztes Wort für „verborgen" — die App reicht es aus paraglide durch.
   * Der Default wird bei jedem Render neu ausgewertet (Parameter-Default, kein
   * Modul-Scope), folgt also einem Sprachwechsel. Vorher hart 'Gizli'.
   */
  maskedLabel = m.privacy_masked(),
}: {
  children: ReactNode
  maskedLabel?: string
}) {
  const hidden = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
  const toggle = useCallback(() => setStored(!getSnapshot()), [])
  const value = useMemo(
    () => ({
      hidden,
      toggle,
      maskedLabel,
      // Nur die Ziffern werden ersetzt, Währungszeichen und Beitext bleiben:
      // "₺1.234,50" -> "₺✱✱✱✱", "Ocak · ₺12.480" -> "Ocak · ₺✱✱✱✱",
      // "249.9 → 299.9" -> "✱✱✱✱ → ✱✱✱✱". Sonst stünde neben einer <Amount>
      // mit "₺✱✱✱✱" ein nacktes "✱✱✱✱" — zwei Schreibweisen im selben Widget.
      mask: (text: string) => (hidden ? text.replace(/\d[\d.,]*/g, MASK) : text),
    }),
    [hidden, toggle, maskedLabel],
  )
  return <PrivacyContext.Provider value={value}>{children}</PrivacyContext.Provider>
}

// ─── Formatierung ──────────────────────────────────────────────────────────

/**
 * decimals setzt beide Grenzen. min/max getrennt gibt es, weil im Bestand
 * beide Halbformen vorkommen: `{ minimumFractionDigits: 2 }` allein lässt bis
 * zu 3 Nachkommastellen zu, `{ maximumFractionDigits: 2 }` allein lässt glatte
 * Beträge ohne Komma stehen. Wer eine solche Stelle umstellt, bildet sie damit
 * exakt ab statt die Anzeige still zu verändern.
 *
 * Fallstrick (21.07.2026, von der Gegenprobe gefunden): die Obergrenze darf
 * NICHT aus `decimals` abgeleitet werden, wenn nur `minDecimals` gesetzt ist —
 * mit dem Default decimals=2 kam sonst wieder max=2 heraus und `minDecimals`
 * war wirkungslos. Intls eigener Default für die Obergrenze ist max(min, 3),
 * genau den bildet der Zweig unten nach.
 */
export function formatAmount(
  value: number,
  decimals = 2,
  locale = 'tr-TR',
  minDecimals?: number,
  maxDecimals?: number,
): string {
  const min = minDecimals ?? decimals
  const max = maxDecimals ?? (minDecimals !== undefined ? Math.max(min, 3) : decimals)
  return value.toLocaleString(locale, {
    minimumFractionDigits: min,
    maximumFractionDigits: Math.max(min, max),
  })
}

// ─── <Amount> ──────────────────────────────────────────────────────────────

export interface AmountProps {
  /** Betrag in ganzen Lira (nicht kuruş-minor). */
  value: number
  /** Nachkommastellen (default 2). */
  decimals?: number
  /** Vorsatz, default '₺'. Für Vorzeichen: prefix="-₺" oder "~₺". */
  prefix?: string
  /** Nachsatz, z.B. ' TL' oder ' /ay'. */
  suffix?: string
  /** Zahlen-Locale (default 'tr-TR'). */
  locale?: string
  /** Spring-Animation beim Erscheinen (ersetzt <AnimatedNumber prefix="₺">). */
  animated?: boolean
  className?: string
  /** Nur für animated: Sekunden (default 0.8). */
  duration?: number
  /** Nur für animated: erst animieren wenn sichtbar (default true). */
  animateOnView?: boolean
  /**
   * Untergrenze der Nachkommastellen, wenn die Vorlage nur
   * `minimumFractionDigits` gesetzt hatte (Obergrenze bleibt dann Intl-Default).
   */
  minDecimals?: number
  /** Obergrenze, wenn die Vorlage nur `maximumFractionDigits` gesetzt hatte. */
  maxDecimals?: number
}

/**
 * Einzige Geldanzeige des Panels. Im Gizli mod erscheint `₺✱✱✱✱` an derselben
 * Stelle mit derselben Breite.
 */
export function Amount({
  value,
  decimals = 2,
  prefix = '₺',
  suffix = '',
  locale = 'tr-TR',
  animated = false,
  className = '',
  duration,
  animateOnView,
  minDecimals,
  maxDecimals,
}: AmountProps) {
  const { hidden, maskedLabel } = usePrivacy()
  const text = formatAmount(value, decimals, locale, minDecimals, maxDecimals)

  // Reveal-Animation (User 24.07.2026 abends): beim Ausschalten des Gizli-Mods
  // ließ NUR die Haftalık-Ciro-Kachel die Zahlen durchlaufen — sie nutzt
  // animated={true}, alle anderen zeigten den Betrag schlagartig. Zentral hier
  // statt pro Kachel (F96-Geist: EINE Geldanzeige): der Übergang maskiert →
  // sichtbar remountet einmalig den AnimatedNumber-Pfad (key=revealNonce),
  // danach verhält sich die Stelle wieder wie zuvor konfiguriert.
  const [revealNonce, setRevealNonce] = useState(0)
  const prevHidden = useRef(hidden)
  useEffect(() => {
    if (prevHidden.current && !hidden) setRevealNonce((n) => n + 1)
    prevHidden.current = hidden
  }, [hidden])

  if (hidden) {
    // Breite des echten Textes reservieren, damit Kacheln nicht springen.
    // Kein title: das Elternelement trägt oft einen eigenen Tooltip, und zwei
    // konkurrierende titles zeigen je nach Cursorposition mal so, mal so.
    // Kein text-center: die Maske ist kürzer als der echte Betrag und rückte
    // damit in links- und rechtsbündigen Spalten sichtbar vom Rand ab. Ohne die
    // Klasse erbt sie die Ausrichtung der Umgebung und bleibt an ihrem Platz.
    return (
      <span
        className={`inline-block tabular-nums ${className}`}
        style={{ minWidth: `${prefix.length + text.length + suffix.length}ch` }}
        aria-label={maskedLabel}
      >
        {prefix}
        {MASK}
        {suffix}
      </span>
    )
  }

  if (animated || revealNonce > 0) {
    // Reveal-Remounts (revealNonce-Pfad) animieren OHNE useInView-Gate:
    // animateOnView={false} startet die Spring sofort im Effect. Grund
    // 25.07.2026: Das Gate hängt am IntersectionObserver, und der liefert
    // je nach Timing/Compositing erst spät oder gar nicht (Debug-Beweis:
    // frischer IO auf sichtbarem Element ohne Callback im eingefrorenen
    // Pane) — der Betrag bliebe dann auf ₺0 stehen. Der Reveal passiert
    // per Definition im sichtbaren Viewport, ein Sichtbarkeits-Gate hat
    // hier keinen Nutzen. animated=true-Kacheln behalten ihr Verhalten.
    return (
      <AnimatedNumber
        // key erzwingt den Neu-Mount je Reveal — sonst spränge eine bereits
        // gemountete AnimatedNumber ohne Durchlauf auf den Endwert.
        key={animated ? undefined : `reveal-${revealNonce}`}
        value={value}
        decimals={decimals}
        prefix={prefix}
        suffix={suffix}
        locale={locale}
        className={className}
        {...(duration !== undefined ? { duration } : {})}
        {...(animated
          ? animateOnView !== undefined
            ? { animateOnView }
            : {}
          : { animateOnView: false })}
      />
    )
  }

  return (
    <span className={className}>
      {prefix}
      {text}
      {suffix}
    </span>
  )
}
